import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Calendar, BookMarked, DoorOpen, Building2, Users, Monitor, Settings, LogOut, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";

const navItem = "flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all hover:bg-slate-100";
const navActive = "bg-slate-900 text-white hover:bg-slate-900";

const links = [
  { to: "/", label: "Kalender", icon: Calendar, end: true, testid: "nav-calendar" },
  { to: "/bookings", label: "Mine bookinger", icon: BookMarked, testid: "nav-bookings" },
];

const adminLinks = [
  { to: "/admin/rooms", label: "Lokaler", icon: DoorOpen, testid: "nav-admin-rooms" },
  { to: "/admin/companies", label: "Virksomheder", icon: Building2, testid: "nav-admin-companies" },
  { to: "/admin/users", label: "Brugere", icon: Users, testid: "nav-admin-users" },
  { to: "/admin/displays", label: "Display skærme", icon: Monitor, testid: "nav-admin-displays" },
  { to: "/admin/settings", label: "Indstillinger", icon: Settings, testid: "nav-admin-settings" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const allLinks = [...links, ...(user?.role === "admin" ? adminLinks : [])];

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar - desktop */}
      <aside className="hidden md:flex w-64 bg-white border-r border-slate-200 flex-col">
        <div className="px-6 py-7">
          <h1 className="font-display text-2xl font-bold text-slate-900 tracking-tight">Lokalebooking</h1>
          <p className="text-xs text-slate-500 mt-1">Fælleslokaler · Kontorhus</p>
        </div>
        <nav className="flex-1 px-3 space-y-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={l.testid}
              className={({ isActive }) => `${navItem} ${isActive ? navActive : "text-slate-700"}`}
            >
              <l.icon className="h-4 w-4" />
              {l.label}
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <>
              <div className="px-4 pt-6 pb-2 text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
                Administration
              </div>
              {adminLinks.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={l.testid}
                  className={({ isActive }) => `${navItem} ${isActive ? navActive : "text-slate-700"}`}
                >
                  <l.icon className="h-4 w-4" />
                  {l.label}
                </NavLink>
              ))}
            </>
          )}
        </nav>
        <div className="p-4 border-t border-slate-100">
          <div className="mb-3 px-2">
            <p className="text-sm font-semibold text-slate-900 truncate" data-testid="current-user-name">{user?.name}</p>
            <p className="text-xs text-slate-500 truncate">{user?.email}</p>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start gap-3 rounded-xl"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut className="h-4 w-4" />
            Log ud
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-30 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <h1 className="font-display font-bold text-lg text-slate-900">Lokalebooking</h1>
        <button
          className="p-2 rounded-lg hover:bg-slate-100"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile menu drawer */}
      {open && (
        <div className="md:hidden fixed inset-0 z-40 bg-black/40" onClick={() => setOpen(false)}>
          <div
            className="absolute top-14 right-0 left-0 bg-white shadow-xl rounded-b-2xl p-4 space-y-1"
            onClick={(e) => e.stopPropagation()}
          >
            {allLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                onClick={() => setOpen(false)}
                data-testid={`m-${l.testid}`}
                className={({ isActive }) => `${navItem} ${isActive ? navActive : "text-slate-700"}`}
              >
                <l.icon className="h-4 w-4" />
                {l.label}
              </NavLink>
            ))}
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 rounded-xl mt-2"
              onClick={handleLogout}
              data-testid="m-logout-button"
            >
              <LogOut className="h-4 w-4" />
              Log ud ({user?.email})
            </Button>
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="flex-1 md:ml-0 pt-16 md:pt-0">
        <div className="max-w-[1400px] mx-auto p-4 md:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
